import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import { promotionPeriodsService } from "../../services/promotion-periods.service";
import { PromotionPeriod, CreatePromotionPeriodDto } from "../../interfaces/promotion-period.interface";
import { useConfirm } from "../../hooks/useConfirm";

const EMPTY_FORM: CreatePromotionPeriodDto = { name: "", startAt: "", endAt: "" };

function getStatus(p: PromotionPeriod): "active" | "scheduled" | "ended" | "inactive" {
  if (!p.isActive) return "inactive";
  const now = new Date();
  if (now >= new Date(p.startAt) && now <= new Date(p.endAt)) return "active";
  if (now < new Date(p.startAt)) return "scheduled";
  return "ended";
}

function formatDt(iso: string) {
  return new Date(iso).toLocaleString();
}

function getErrMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as any).response?.data;
    if (r?.message) return Array.isArray(r.message) ? r.message.join(", ") : r.message;
  }
  return fallback;
}

const STATUS_CLASS: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  ended: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  inactive: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
};

const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

const PromotionPeriodsPage = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();

  const [rows, setRows] = useState<PromotionPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PromotionPeriod | null>(null);
  const [form, setForm] = useState<CreatePromotionPeriodDto>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const setF = (patch: Partial<CreatePromotionPeriodDto>) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await promotionPeriodsService.list(1, 100);
      setRows(res.data);
    } catch {
      setError(t("load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (p: PromotionPeriod) => {
    setEditing(p);
    setForm({ name: p.name, startAt: p.startAt.slice(0, 16), endAt: p.endAt.slice(0, 16) });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        await promotionPeriodsService.update(editing.id, form);
      } else {
        await promotionPeriodsService.create(form);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(getErrMsg(err, t("save_error")));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (p: PromotionPeriod) => {
    const key = p.isActive ? "confirm_deactivate" : "confirm_activate";
    const ok = await confirm(t(key));
    if (!ok) return;
    try {
      await promotionPeriodsService.update(p.id, { isActive: !p.isActive });
      await load();
    } catch {
      setError(t("save_error"));
    }
  };

  const handleDelete = async (p: PromotionPeriod) => {
    const ok = await confirm(t("confirmDelete"));
    if (!ok) return;
    try {
      await promotionPeriodsService.remove(p.id);
      await load();
    } catch {
      setError(t("delete_error"));
    }
  };

  return (
    <Layout>
      {ConfirmDialog}
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t("promotion_periods")}</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("promotion_periods_desc")}</p>
          </div>
          <Button onClick={openCreate}>{t("add_promotion_period")}</Button>
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}

        <Card>
          {loading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">{t("loading")}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">{t("no_records")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("name")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("start_at")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("end_at")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("status")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const status = getStatus(p);
                    return (
                      <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{p.name}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDt(p.startAt)}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDt(p.endAt)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[status]}`}>
                            {t(`promo_status_${status}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <button onClick={() => handleToggleActive(p)} className="text-yellow-600 dark:text-yellow-400 hover:underline text-xs">
                            {p.isActive ? t("deactivate") : t("activate")}
                          </button>
                          <button onClick={() => openEdit(p)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">{t("edit")}</button>
                          <button onClick={() => handleDelete(p)} className="text-red-500 dark:text-red-400 hover:underline text-xs">{t("delete")}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={showForm} onClose={() => setShowForm(false)} title={editing ? t("edit_promotion_period") : t("add_promotion_period")}>
          <div className="space-y-4">
            {formError && (
              <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">{formError}</div>
            )}
            <div>
              <label className={labelClass}>{t("name")} *</label>
              <input className={inputClass} value={form.name} onChange={(e) => setF({ name: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("start_at")} *</label>
              <input type="datetime-local" className={inputClass} value={form.startAt} onChange={(e) => setF({ startAt: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("end_at")} *</label>
              <input type="datetime-local" className={inputClass} value={form.endAt} onChange={(e) => setF({ endAt: e.target.value })} />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>{t("cancel")}</Button>
              <Button onClick={handleSubmit} disabled={saving || !form.name || !form.startAt || !form.endAt}>
                {saving ? t("saving") : t("save")}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PromotionPeriodsPage;
